import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DecimalInput } from '@/components/ui/DecimalInput'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { LineRequestDto, LineResponseDto, ProjectCategoryDto, ProjectSalesVatsDto } from './types'
import {
  addQuoteVersionLineV2,
  getProjectQuoteCategoriesV2,
  getProjectSalesVatsV2,
  updateQuoteVersionLineV2,
} from './quotes-v2.api'
import { pickableCategories } from './category-options'
import { parseDecimalInput } from '@/lib/decimal'

interface DiscountLineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quoteId: string | null
  versionId: string | null
  mode: 'create' | 'update'
  lineId?: string | null
  initialLine?: LineResponseDto | null
  onSubmitted: () => void | Promise<void>
}

export function DiscountLineDialog({
  open,
  onOpenChange,
  projectId,
  quoteId,
  versionId,
  mode,
  lineId,
  initialLine,
  onSubmitted,
}: DiscountLineDialogProps) {
  const [categories, setCategories] = useState<ProjectCategoryDto[]>([])
  const [vats, setVats] = useState<ProjectSalesVatsDto | null>(null)
  const [title, setTitle] = useState<string>('')
  const [titleDirty, setTitleDirty] = useState(false)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [vatPercent, setVatPercent] = useState<number | null>(null)
  const [amount, setAmount] = useState<number | null>(null)
  const [amountRaw, setAmountRaw] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    let isMounted = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const [categoriesResponse, vatsResponse] = await Promise.all([
          getProjectQuoteCategoriesV2(projectId),
          getProjectSalesVatsV2(projectId),
        ])

        if (!isMounted) return

        const cats = categoriesResponse.categories || []
        setCategories(cats)
        setVats(vatsResponse)
        const nextVatPercent =
          mode === 'update' && typeof initialLine?.vatPercent === 'number'
            ? initialLine.vatPercent
            : vatsResponse.defaultVatPercent

        setVatPercent(nextVatPercent)

        const nextCategoryId = mode === 'update' ? (initialLine?.categoryId ?? null) : null
        setCategoryId(nextCategoryId)

        const nextAmount =
          mode === 'update' && typeof initialLine?.unitPrice === 'number'
            ? Math.abs(initialLine.unitPrice)
            : null
        setAmount(nextAmount)

      } catch {
        if (isMounted) {
          setError("Erreur lors du chargement")
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId])

  const selectedCategory = useMemo(() => {
    if (!categoryId) return null
    return categories.find((c) => c.id === categoryId) || null
  }, [categories, categoryId])

  const derivedTitle = useMemo(() => {
    return selectedCategory ? `Remise • ${selectedCategory.label}` : 'Remise'
  }, [selectedCategory])

  useEffect(() => {
    if (!open) return
    setTitleDirty(false)
    if (mode === 'update') {
      setTitle(typeof initialLine?.title === 'string' ? initialLine.title : '')
      return
    }
    setTitle(derivedTitle)
  }, [open, mode, initialLine?.title, derivedTitle])

  useEffect(() => {
    if (!open) return
    if (mode !== 'create') return
    if (titleDirty) return
    setTitle(derivedTitle)
  }, [open, mode, titleDirty, derivedTitle])

  useEffect(() => {
    if (!open) return
    setAmountRaw(typeof amount === 'number' && Number.isFinite(amount) ? String(amount) : '')
  }, [open, amount])

  const normalizedAmount = useMemo(() => {
    const n = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0
    return Math.abs(n)
  }, [amount])

  const previewUnitPrice = useMemo(() => {
    return -normalizedAmount
  }, [normalizedAmount])

  const trimmedTitle = title.trim()

  const canSubmit =
    !!quoteId &&
    !!versionId &&
    !isLoading &&
    !isSubmitting &&
    vats != null &&
    vatPercent != null &&
    trimmedTitle.length > 0 &&
    normalizedAmount > 0 &&
    (mode === 'create' || !!lineId)

  const handleSubmit = async () => {
    if (!quoteId || !versionId) {
      setError("Erreur: contexte du devis manquant")
      return
    }
    if (!vats || vatPercent == null) return
    if (trimmedTitle.length === 0) {
      setError('Le titre est requis')
      return
    }
    if (mode === 'update' && !lineId) {
      setError("Erreur: ligne introuvable")
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const request: LineRequestDto = {
        kind: 'discount',
        title: trimmedTitle,
        description: null,
        categoryId: categoryId,
        categoryLabel: selectedCategory?.label ?? null,
        familyId: null,
        familyKey: null,
        attributes: null,
        quantity: 1,
        unitPrice: previewUnitPrice,
        vatPercent,
        amount: null,
      }

      if (mode === 'update' && lineId) {
        await updateQuoteVersionLineV2(projectId, quoteId, versionId, lineId, request)
      } else {
        await addQuoteVersionLineV2(projectId, quoteId, versionId, request)
      }
      await onSubmitted()
      onOpenChange(false)
    } catch {
      setError(mode === 'update' ? "Erreur lors de la mise à jour de la remise" : "Erreur lors de l'ajout de la remise")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{mode === 'update' ? 'Modifier la remise' : 'Ajouter une remise'}</DialogTitle>
          <DialogDescription>
            Montant fixe. Le montant sera appliqué en négatif. La TVA sélectionnée s'appliquera à cette ligne.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Chargement...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={categoryId ?? '__all__'} onValueChange={(v) => setCategoryId(v === '__all__' ? null : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Toutes catégories</SelectItem>
                    {pickableCategories(categories, initialLine?.categoryId).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>TVA</Label>
                <Select
                  value={vatPercent != null ? String(vatPercent) : undefined}
                  onValueChange={(v) => setVatPercent(parseInt(v, 10))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {(vats?.availableVats || []).map((opt: ProjectSalesVatsDto['availableVats'][number]) => (
                      <SelectItem key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  TVA appliquée: <span className="font-medium">{vatPercent != null ? `${vatPercent}%` : '-'}</span>
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Titre
                <span className="text-muted-foreground"> *</span>
              </Label>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value)
                  setTitleDirty(true)
                }}
                placeholder={derivedTitle}
              />
            </div>

            <div className="space-y-2">
              <Label>Montant HT</Label>
              <div className="flex items-center gap-2">
                <DecimalInput
                  value={amountRaw}
                  onValueChange={setAmountRaw}
                  onBlur={() => {
                    const parsed = parseDecimalInput(amountRaw)
                    setAmount(parsed)
                  }}
                  placeholder=""
                />
                <span className="text-sm text-muted-foreground">€</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Sera enregistré comme: <span className="font-medium">{previewUnitPrice.toFixed(2)} €</span>
              </p>
            </div>

            <div className="border-t pt-3 text-xs text-muted-foreground">
              Qté: <span className="font-medium">1</span>
              {' • '}
              PU: <span className="font-medium">{previewUnitPrice.toFixed(2)} €</span>
              {' • '}
              TVA: <span className="font-medium">{vatPercent != null ? `${vatPercent}%` : '-'}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting
              ? (mode === 'update' ? 'Enregistrement...' : 'Ajout...')
              : (mode === 'update' ? 'Enregistrer' : 'Ajouter')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
